import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

const BANNER_URL = 'https://iis.memphis.edu/wp-content/uploads/2025/03/cropped-UofM_horiz_rgb.jpg';

const PasswordResetPage = () => {
  const apiPrefix = window.location.pathname.match(/^\/[^\/]+/) ? window.location.pathname.match(/^\/[^\/]+/)[0].replace(/\/$/, '') : '';
    const location = useLocation();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [tempPassword, setTempPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userId, setUserId] = useState(null);

    // Check if we have userId and email from navigation state (legacy flow)
    useEffect(() => {
        if (location.state?.userId && location.state?.email) {
            setUserId(location.state.userId);
            setEmail(location.state.email);
            setIsAuthenticated(true);
        }
    }, [location]);

    // Password strength checker
    useEffect(() => {
        if (!newPassword) {
            setPasswordStrength(0);
            return;
        }
        let strength = 0;
        if (newPassword.length >= 6) strength += 20;
        if (newPassword.length >= 12) strength += 20;
        if (/[a-z]/.test(newPassword)) strength += 20;
        if (/[A-Z]/.test(newPassword)) strength += 20;
        if (/[0-9]/.test(newPassword)) strength += 20;
        setPasswordStrength(Math.min(strength, 100));
    }, [newPassword]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);

        // If not yet authenticated, verify temp password first
        if (!isAuthenticated) {
            if (!email || !tempPassword) {
                setError('Email and temporary password are required');
                return;
            }

            setLoading(true);

            try {
                // Verify temp password
                const loginResponse = await fetch(apiPrefix + '/api/login-user', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email, password: tempPassword })
                });

                const loginData = await loginResponse.json();

                if (!loginResponse.ok || !loginData.success) {
                    setError(loginData.error || 'Invalid email or temporary password');
                    setLoading(false);
                    return;
                }

                // Check if password reset is required
                if (!loginData.requiresPasswordReset) {
                    setError('This account does not require a password reset');
                    setLoading(false);
                    return;
                }

                // Store userId and mark as authenticated
                setUserId(loginData.userId);
                setIsAuthenticated(true);
                setLoading(false);
                return;
            } catch (err) {
                setError('An error occurred. Please try again.');
                console.error('Error:', err);
                setLoading(false);
                return;
            }
        }

        // If authenticated, proceed with password change
        if (!newPassword || !confirmPassword) {
            setError('Both password fields are required');
            return;
        }

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(apiPrefix + '/api/change-password-temp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId,
                    newPassword,
                    newPasswordConfirm: confirmPassword
                })
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || 'Failed to reset password');
                setLoading(false);
                return;
            }

            setSuccess(true);
            setNewPassword('');
            setConfirmPassword('');

            // Store the JWT token and redirect to Overleaf
            if (data.token) {
                localStorage.setItem('jwt_token', data.token);
                localStorage.setItem('user', JSON.stringify({
                    email: data.email,
                    userId: data.userId,
                    isAdmin: data.isAdmin
                }));
                
                // Redirect to Overleaf after a short delay
                setTimeout(() => {
                    window.location.href = '/';
                }, 2000);
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength < 40) return 'danger';
        if (passwordStrength < 70) return 'warning';
        return 'success';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength < 40) return 'Weak';
        if (passwordStrength < 70) return 'Fair';
        return 'Good';
    };

    if (success) {
        return (
            <div className="min-vh-100 bg-light d-flex align-items-center">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-md-6">
                            <div className="card shadow">
                                <div className="card-body p-5 text-center">
                                    <div className="mb-3">
                                        <i className="bi bi-check-circle text-success" style={{ fontSize: '3rem' }}></i>
                                    </div>
                                    <h4 className="card-title mb-3">Password Reset Successful!</h4>
                                    <p className="text-muted">
                                        Your password has been updated successfully. You will be redirected to the dashboard shortly.
                                    </p>
                                    <div className="spinner-border text-success mt-4" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-vh-100 bg-light">
            <nav className="navbar navbar-expand-lg navbar-white bg-white border-bottom">
                <div className="container">
                    <a className="navbar-brand" href="/">
                        <img
                            src={BANNER_URL}
                            height="40"
                            className="d-inline-block align-top"
                            alt="University of Memphis"
                        />
                    </a>
                </div>
            </nav>

            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-md-6">
                        <div className="card shadow">
                            <div className="card-body p-5">
                                {!isAuthenticated ? (
                                    <>
                                        <h2 className="card-title mb-2 text-center">Login with Temporary Password</h2>
                                        <p className="text-muted text-center mb-4 small">
                                            Enter your email and the temporary password you received to set a new password.
                                        </p>

                                        {error && (
                                            <div className="alert alert-danger" role="alert">
                                                <i className="bi bi-exclamation-circle me-2"></i>
                                                {error}
                                            </div>
                                        )}

                                        <form onSubmit={handleSubmit}>
                                            <div className="mb-3">
                                                <label htmlFor="email" className="form-label">
                                                    Email Address
                                                </label>
                                                <input
                                                    type="email"
                                                    className="form-control"
                                                    id="email"
                                                    placeholder="Enter your email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    disabled={loading}
                                                    required
                                                />
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="tempPassword" className="form-label">
                                                    Temporary Password
                                                </label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    id="tempPassword"
                                                    placeholder="Enter temporary password"
                                                    value={tempPassword}
                                                    onChange={(e) => setTempPassword(e.target.value)}
                                                    disabled={loading}
                                                    required
                                                />
                                                <small className="form-text text-muted d-block mt-2">
                                                    This was provided to you by the administrator
                                                </small>
                                            </div>

                                            <button
                                                type="submit"
                                                className="btn btn-primary w-100"
                                                disabled={loading || !email || !tempPassword}
                                                style={{ backgroundColor: '#138A07', borderColor: '#138A07' }}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Verifying...
                                                    </>
                                                ) : (
                                                    'Continue'
                                                )}
                                            </button>
                                        </form>
                                    </>
                                ) : (
                                    <>
                                        <h2 className="card-title mb-2 text-center">Set Your New Password</h2>
                                        <p className="text-muted text-center mb-4 small">
                                            You are required to set a new password before continuing.
                                        </p>
                                        {email && (
                                            <p className="text-muted text-center mb-4 small">
                                                <strong>Account:</strong> {email}
                                            </p>
                                        )}

                                        {error && (
                                            <div className="alert alert-danger" role="alert">
                                                <i className="bi bi-exclamation-circle me-2"></i>
                                                {error}
                                            </div>
                                        )}

                                        <form onSubmit={handleSubmit}>
                                            <div className="mb-3">
                                                <label htmlFor="newPassword" className="form-label">
                                                    New Password
                                                </label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    id="newPassword"
                                                    placeholder="Enter new password"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    disabled={loading}
                                                    required
                                                />
                                                {newPassword && (
                                                    <div className="mt-2">
                                                        <small className="text-muted">Password strength: </small>
                                                        <div className="progress" style={{ height: '5px' }}>
                                                            <div
                                                                className={`progress-bar bg-${getPasswordStrengthColor()}`}
                                                                role="progressbar"
                                                                style={{ width: `${passwordStrength}%` }}
                                                                aria-valuenow={passwordStrength}
                                                                aria-valuemin="0"
                                                                aria-valuemax="100"
                                                            />
                                                        </div>
                                                        <small className={`text-${getPasswordStrengthColor()}`}>
                                                            {getPasswordStrengthText()}
                                                        </small>
                                                    </div>
                                                )}
                                                <small className="form-text text-muted d-block mt-2">
                                                    Minimum 6 characters
                                                </small>
                                            </div>

                                            <div className="mb-3">
                                                <label htmlFor="confirmPassword" className="form-label">
                                                    Confirm Password
                                                </label>
                                                <input
                                                    type="password"
                                                    className="form-control"
                                                    id="confirmPassword"
                                                    placeholder="Confirm new password"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    disabled={loading}
                                                    required
                                                />
                                                {newPassword && confirmPassword && (
                                                    <small className="d-block mt-2">
                                                        {newPassword === confirmPassword ? (
                                                            <span className="text-success">✓ Passwords match</span>
                                                        ) : (
                                                            <span className="text-danger">✗ Passwords do not match</span>
                                                        )}
                                                    </small>
                                                )}
                                            </div>

                                            <button
                                                type="submit"
                                                className="btn btn-primary w-100"
                                                disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                                                style={{ backgroundColor: '#138A07', borderColor: '#138A07' }}
                                            >
                                                {loading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Setting Password...
                                                    </>
                                                ) : (
                                                    'Set New Password'
                                                )}
                                            </button>
                                        </form>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PasswordResetPage;
